/**
 * Соединение с game-server и состояние клиента.
 * Клиент не принимает игровых решений: он только отображает персональное представление,
 * присланное сервером, и отправляет команды. Время этапов — по серверным часам (смещение).
 */
import { useSyncExternalStore } from 'react';
import type {
  ActionType,
  AutoAction,
  ChartPayload,
  ClientMessage,
  Direction,
  GameView,
  Minor,
  Msg,
  PlayerProfile,
  RoomSummary,
  RoomView,
  ServerInfo,
  ServerMessage,
} from '@mt/protocol';
import { t, tm } from '../i18n';
import { CLOSE_HEARTBEAT, CLOSE_NO_HELLO, pollTransport, wsTransport, type Transport, type TransportHandlers, type TransportKind } from './transport';

export type ConnState = 'connecting' | 'open' | 'closed';

export interface Toast {
  id: number;
  kind: 'error' | 'info';
  text: string;
}

export interface AppState {
  conn: ConnState;
  /** диагностика соединения: транспорт, адрес, код последнего закрытия, число неудачных попыток подряд */
  connInfo: { transport: TransportKind; url: string; lastCode: number | null; attempts: number };
  info: ServerInfo | null;
  profile: PlayerProfile | null;
  /** роль текущей сессии: игрок или администратор (не играет) */
  role: 'player' | 'admin' | null;
  adminLogin: string | null;
  /** текст сервера о завершённой сессии (русский, совместимость) и его код для показа на выбранном языке */
  authError: string | null;
  authMsg: Msg | null;
  needLogin: boolean;
  lobby: RoomSummary[];
  room: RoomView | null;
  game: GameView | null;
  chart: ChartPayload | null;
  toasts: Toast[];
  pending: Set<string>;
}

const TOKEN_KEY = 'mt.token';
const TRANSPORT_KEY = 'mt.transport';
const TRANSPORT_TTL_MS = 12 * 3600_000;
/** WebSocket, проживший меньше, считается неработающим (рвётся сразу после установки) */
const WS_MIN_HEALTHY_MS = 30_000;
/** сервер присылает hello сразу после открытия; нет его за это время — транспорт не работает */
const HELLO_TIMEOUT_MS = 5_000;
/** сколько ждать подтверждения действия, прежде чем снова разблокировать кнопки хода */
const ACK_TIMEOUT_MS = 5_000;

/** Запомненный резервный транспорт (если WebSocket в этой сети не работает). */
function rememberedTransport(): TransportKind | null {
  try {
    const v = JSON.parse(localStorage.getItem(TRANSPORT_KEY) ?? 'null') as { kind: TransportKind; until: number } | null;
    return v && v.until > Date.now() ? v.kind : null;
  } catch {
    return null;
  }
}
function rememberTransport(kind: TransportKind | null) {
  try {
    if (kind) localStorage.setItem(TRANSPORT_KEY, JSON.stringify({ kind, until: Date.now() + TRANSPORT_TTL_MS }));
    else localStorage.removeItem(TRANSPORT_KEY);
  } catch {
    /* хранилище недоступно — выбор действует до перезагрузки страницы */
  }
}

/** Токен сессии для HTTP-запросов (профиль, история, администрирование, пакеты проверки). */
export function authToken(): string | null {
  return readToken();
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}
function writeToken(t: string | null) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* хранилище недоступно — вход сохранится только на время вкладки */
  }
}

class Client {
  state: AppState = {
    conn: 'connecting',
    connInfo: { transport: 'ws', url: '', lastCode: null, attempts: 0 },
    info: null,
    profile: null,
    role: null,
    adminLogin: null,
    authError: null,
    authMsg: null,
    needLogin: !readToken(),
    lobby: [],
    room: null,
    game: null,
    chart: null,
    toasts: [],
    pending: new Set(),
  };
  private listeners = new Set<() => void>();
  private transport: Transport | null = null;
  private retry = 0;
  /** подряд неудачных WebSocket-соединений: не открылось, не прислало ни одного сообщения,
   * умерло по проверке живости или прожило меньше WS_MIN_HEALTHY_MS */
  private wsFailures = 0;
  /** учёт завершения текущего соединения (для drop) */
  private endCurrent: ((code: number) => void) | null = null;
  private lastMessageAt = 0;
  private lastPingAt = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private toastSeq = 0;
  private actionSeq = 0;
  /** serverNow − Date.now() */
  offset = 0;

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getState = () => this.state;

  private set(patch: Partial<AppState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  now(): number {
    return Date.now() + this.offset;
  }

  /**
   * Подключение: сначала WebSocket; если он дважды подряд не заработал (не открылся, «висит»
   * на рукопожатии, не доставил ни одного сообщения или постоянно рвётся), — HTTP long-polling. Выбор резервного
   * транспорта запоминается на 12 ч. Принудительно: ?transport=poll или ?transport=ws в адресе.
   */
  connect() {
    const kind = this.pickTransport();
    let opened = false;
    let received = false;
    let openedAt = 0;
    const end = (code: number) => {
      if (kind === 'ws' && code !== 1000) {
        const healthy = received && code !== CLOSE_HEARTBEAT && Date.now() - openedAt >= WS_MIN_HEALTHY_MS;
        this.wsFailures = healthy ? 0 : this.wsFailures + 1;
        if (this.wsFailures >= 2) rememberTransport('poll');
      }
      if (kind === 'poll' && !opened) {
        // резерв тоже не открылся — скорее всего, недоступен сам сервер: следующая попытка снова через WebSocket
        this.wsFailures = 0;
        rememberTransport(null);
      }
    };
    const handlers: TransportHandlers = {
      // Транспорт открыт, но рабочим соединение считается только после первого сообщения сервера
      // (hello): некоторые прокси пропускают рукопожатие WebSocket, но не данные.
      open: () => {
        if (this.transport !== t) return;
        opened = true;
        openedAt = Date.now();
        setTimeout(() => {
          if (this.transport === t && !received) this.drop(CLOSE_NO_HELLO);
        }, HELLO_TIMEOUT_MS);
      },
      message: (m) => {
        if (this.transport !== t) return;
        if (!received) {
          received = true;
          this.retry = 0;
          this.set({ conn: 'open', connInfo: { ...this.state.connInfo, lastCode: null, attempts: 0 } });
          const token = readToken();
          if (token) t.send({ type: 'auth', token });
          this.ping();
        }
        this.lastMessageAt = Date.now();
        this.onMessage(m);
      },
      close: (code) => {
        if (this.transport !== t) return;
        this.transport = null;
        this.endCurrent = null;
        end(code);
        // подтверждения отправленных действий по оборванному соединению уже не придут — не блокируем кнопки
        this.set({ conn: 'closed', pending: new Set(), connInfo: { ...this.state.connInfo, lastCode: code, attempts: this.state.connInfo.attempts + 1 } });
        const switching = kind === 'ws' && this.pickTransport() === 'poll';
        const delay = switching ? 0 : Math.min(10_000, 500 * 2 ** this.retry++);
        setTimeout(() => this.connect(), delay);
      },
    };
    const t: Transport = kind === 'ws' ? wsTransport(handlers) : pollTransport(handlers);
    this.transport = t;
    this.endCurrent = end;
    this.set({ conn: 'connecting', connInfo: { ...this.state.connInfo, transport: kind, url: t.url } });
    this.startHeartbeat();
  }

  private pickTransport(): TransportKind {
    const forced = new URLSearchParams(location.search).get('transport');
    if (forced === 'ws' || forced === 'poll') return forced;
    if (this.wsFailures >= 2) return 'poll';
    return rememberedTransport() ?? 'ws';
  }

  private ping() {
    this.lastPingAt = Date.now();
    this.send({ type: 'ping', t: this.lastPingAt });
  }

  /**
   * Проверка живости: ping раз в 15 с. Если на предыдущий ping за 20 с не пришло ни одного сообщения,
   * соединение считается мёртвым (бывает, что прокси пропускает рукопожатие, но не данные).
   * Сравнение с моментом ping, а не с абсолютным временем, не ложно срабатывает в фоновых вкладках,
   * где браузер замедляет таймеры.
   */
  private startHeartbeat() {
    if (this.heartbeat) return;
    this.heartbeat = setInterval(() => {
      if (this.state.conn !== 'open' || !this.transport) return;
      const now = Date.now();
      if (this.lastPingAt && this.lastMessageAt < this.lastPingAt && now - this.lastPingAt > 20_000) {
        this.drop(CLOSE_HEARTBEAT);
        return;
      }
      if (now - this.lastPingAt >= 15_000) this.ping();
    }, 5_000);
  }

  /** Закрыть текущее соединение и переподключиться (как при обрыве). */
  private drop(code: number) {
    const t = this.transport;
    if (!t) return;
    t.dispose();
    this.transport = null;
    this.endCurrent?.(code);
    this.endCurrent = null;
    this.set({ conn: 'closed', pending: new Set(), connInfo: { ...this.state.connInfo, lastCode: code } });
    setTimeout(() => this.connect(), 0);
  }

  private syncClock(serverNow: number, rtt = 0) {
    const est = serverNow + rtt / 2 - Date.now();
    this.offset = this.offset === 0 ? est : this.offset * 0.7 + est * 0.3;
  }

  private onMessage(m: ServerMessage) {
    switch (m.type) {
      case 'hello':
        this.syncClock(m.serverNow);
        return this.set({ info: m.info });
      case 'pong':
        return this.syncClock(m.serverNow, Date.now() - m.t);
      case 'auth.ok':
        if (m.role === 'admin') return this.set({ role: 'admin', adminLogin: m.login, profile: null, needLogin: false, authError: null, authMsg: null });
        return this.set({ role: 'player', adminLogin: null, profile: m.profile, needLogin: false, authError: null, authMsg: null });
      case 'auth.error':
        writeToken(null);
        return this.set({ needLogin: true, authError: m.message, authMsg: m.msg ?? null, profile: null, role: null, adminLogin: null, room: null, game: null, chart: null });
      case 'profile':
        return this.set({ profile: m.profile });
      case 'lobby':
        this.syncClock(m.serverNow);
        return this.set({ lobby: m.rooms });
      case 'room': {
        const chart = m.game && this.state.chart?.gameId === m.game.gameId ? this.state.chart : null;
        return this.set({ room: m.room, game: m.game, chart });
      }
      case 'chart':
        return this.set({ chart: m.chart });
      case 'ack': {
        const pending = new Set(this.state.pending);
        pending.delete(m.actionId);
        this.set({ pending });
        // текст тоста вычисляется в момент показа — на текущем языке
        if (!m.ok && (m.error || m.msg)) this.toast('error', tm(m.msg, m.error ?? ''));
        return;
      }
      case 'error':
        return this.toast('error', tm(m.msg, m.message));
    }
  }

  toast(kind: Toast['kind'], text: string) {
    const t = { id: ++this.toastSeq, kind, text };
    this.set({ toasts: [...this.state.toasts.slice(-3), t] });
    setTimeout(() => this.set({ toasts: this.state.toasts.filter((x) => x.id !== t.id) }), 5000);
  }

  send(m: ClientMessage) {
    if (this.transport && this.state.conn === 'open') this.transport.send(m);
    else this.toast('error', t('app.noConnection'));
  }

  // ── вход ──
  /** Сессия получена по HTTP (вход, регистрация, SSO): сохранить и войти по текущему соединению. */
  signIn(token: string) {
    writeToken(token);
    this.set({ needLogin: false, authError: null, authMsg: null });
    // если связи ещё нет, вход по токену выполнится сразу после установки соединения
    if (this.transport && this.state.conn === 'open') this.transport.send({ type: 'auth', token });
  }
  logout() {
    const token = readToken();
    if (token) void fetch('/api/auth/logout', { method: 'POST', headers: { authorization: `Bearer ${token}` } }).catch(() => {});
    writeToken(null);
    this.set({ needLogin: true, profile: null, role: null, adminLogin: null, room: null, game: null, chart: null });
    this.drop(1000);
  }
  /** Данные профиля изменились по HTTP (имя, видимость статистики) — обновить локально. */
  patchProfile(p: Partial<PlayerProfile>) {
    if (this.state.profile) this.set({ profile: { ...this.state.profile, ...p } });
  }

  // ── команды ──
  join(categoryId: string) {
    this.send({ type: 'room.join', categoryId });
  }
  watch(roomId: string) {
    this.send({ type: 'room.watch', roomId });
  }
  leave() {
    this.send({ type: 'room.leave' });
  }
  ready(ready: boolean) {
    this.send({ type: 'room.ready', ready });
  }
  /**
   * Каждое действие имеет уникальный id: повторная доставка не создаст повторной ставки.
   * Пока сервер не подтвердил действие, кнопки хода заблокированы — но не дольше ACK_TIMEOUT_MS:
   * потерянное подтверждение не должно лишать игрока хода (актуальное состояние придёт с обновлением стола).
   */
  act(action: ActionType, amount?: Minor) {
    if (!this.transport || this.state.conn !== 'open') return this.toast('error', t('app.noConnection'));
    const actionId = `${Date.now().toString(36)}-${++this.actionSeq}`;
    this.set({ pending: new Set(this.state.pending).add(actionId) });
    this.send({ type: 'game.action', actionId, action, amount });
    setTimeout(() => {
      if (!this.state.pending.has(actionId)) return;
      const pending = new Set(this.state.pending);
      pending.delete(actionId);
      this.set({ pending });
    }, ACK_TIMEOUT_MS);
  }
  forecast(f: { direction?: Direction | null; points?: number | null }) {
    this.send({ type: 'game.forecast', ...f });
  }
  auto(auto: AutoAction) {
    this.send({ type: 'game.auto', auto });
  }
}

export const client = new Client();

export function useApp<T>(sel: (s: AppState) => T): T {
  return useSyncExternalStore(client.subscribe, () => sel(client.getState()));
}
